import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PopupTitle from '../common/popup_title';
import MealDetailModal from '../meal/meal_detail_modal';
import EndUserFindAlternativesItem from './end_user_find_alternatives_item';
import { END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_WITH_MEAL_DETAIL_ROUTE } from '../../services/routes';
import { linkWithQueryParams } from '../../services/urls';
import NarrowEndUserMoreButton from '../meal/narrow_end_user_more_button';
import RelaxSearchCriteriaChips from '../common/relax_search_results_chips';
import {
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import { parentIDOfSharedMealID } from '../../services/smorg_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const EndUserFindAlternativesSearchGroupResultsView = ({
  groupResults,
  onBack,
  onDismiss,
  onLoadMore,
  calendarDay,
  mealType,
  mealIDToReplace,
  searchCriteria,
  onChangeSearchCriteria,
}) => {
  const navigate = useNavigate();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const { mealDetailMealID: showDetailForMealID } = useParams();

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const parentIDInSharedMeals = parentIDOfSharedMealID(
    showDetailForMealID,
    sharedBoards,
  );

  const { data: distilledSharedMealsByParents } =
    useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
      skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
    });

  const visibleDistilledMealInSharedMeals = ((distilledSharedMealsByParents ||
    {})[parentIDInSharedMeals] || {})[showDetailForMealID];

  const onClickResultDetail = (mealID) => {
    navigate(
      linkWithQueryParams(
        formatRoute(
          END_USER_FIND_ALTERNATIVES_SEARCH_MEALS_GROUP_RESULTS_WITH_MEAL_DETAIL_ROUTE,
          {
            calendarDay,
            groupLabel: groupResults?.groupLabel,
            mealDetailMealID: mealID,
          },
        ),
        { mealType, mealId: mealIDToReplace },
      ),
    );
  };

  const onDismissDetail = () => navigate(-1);

  console.log({ nextOffset: groupResults.nextOffset });

  const showNutritionMetrics = [
    'calories',
    'fat',
    'carbohydrate',
    'protein',
    'fibre',
  ];

  const alwaysShowNutrition = !isCommunityUser;

  const { data: fullOwnedMeal, isLoading: isFullOwnedMealLoading } =
    useGetFullMealQuery(showDetailForMealID, {
      skip: visibleDistilledMealInSharedMeals,
    });

  const { data: fullSharedMeal, isLoading: isFullSharedMealLoading } =
    useGetFullSharedMealQuery(showDetailForMealID, {
      skip: !visibleDistilledMealInSharedMeals,
    });

  const fullMeal = visibleDistilledMealInSharedMeals
    ? fullSharedMeal
    : fullOwnedMeal;
  const isFullMealLoading = visibleDistilledMealInSharedMeals
    ? isFullSharedMealLoading
    : isFullOwnedMealLoading;

  if (!groupResults) {
    return null;
  }

  const isLoading = isFullMealLoading;

  if (showDetailForMealID && !isLoading && fullMeal) {
    return (
      <MealDetailModal
        fullMeal={fullMeal}
        isLoading={isLoading}
        menu={null}
        isReadOnly
        alwaysShowNutrition={alwaysShowNutrition}
        savingInProgress={false}
        showNutritionMetrics={showNutritionMetrics}
        derivedNutrition={fullMeal?.derivedNutrition}
        canScaleIngredients={false}
        scaleToServings={1}
        currentlyEditingInline={{ section: '', itemId: null }}
        ingredientSuggestions={[]}
        ingredientSuggestionsNetworkState={{ loading: false }}
        onInlineEditFocus={() => {}}
        onInlineEditChange={() => {}}
        onInlineEditBlur={() => {}}
        onAddArraySectionItem={() => {}}
        onRemoveArraySectionItem={() => {}}
        onIngredientCheckChange={() => {}}
        onEditScaleToServings={() => {}}
        onChangeScaleToServings={() => {}}
        resetScaleToServings={() => {}}
        onNewImageChosen={() => {}}
        onRuleChanged={() => {}}
        onAddonChange={() => {}}
        onTagsChange={() => {}}
        onNewImageUrlSet={() => {}}
        onDialogClose={onDismissDetail}
        onUpdateIngredientStructuredData={() => {}}
      />
    );
  }

  return (
    <>
      <div
        className="topNavBarMobile topNavBarMobileLight"
        style={{
          color: 'var(--app-primary-font-color)',
          height: '70px',
          flexDirection: 'column',
        }}>
        <PopupTitle
          hideBorder
          titleText={groupResults.groupLabel}
          closeButtonEnabled
          backButtonEnabled
          onClickBack={onBack}
          onClickClose={onDismiss}
        />
      </div>
      <div className="alternatives-page">
        <Grid container style={{ padding: 10 }}>
          <Grid item xs={12}>
            <h2>{groupResults.groupLabel}</h2>
          </Grid>
          <Grid item xs={12}>
            <RelaxSearchCriteriaChips
              searchCriteria={searchCriteria}
              onChangeSearchCriteria={onChangeSearchCriteria}
            />
          </Grid>
          {!groupResults.isLoading &&
            !groupResults.moreAvailable &&
            groupResults.data.length === 0 && (
              <p style={{ margin: '23px' }}>No results found</p>
            )}
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {groupResults.data.map((result) => {
                const id = result.mealID || result.grcRecipeID;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={id}>
                    <EndUserFindAlternativesItem
                      key={id}
                      mealID={id}
                      calendarDay={calendarDay}
                      mealType={mealType}
                      mealIDToReplace={mealIDToReplace}
                      onClick={onClickResultDetail}
                    />
                  </Grid>
                );
              })}
              {groupResults.isLoading && (
                <Grid item xs={6} sm={4} md={3} lg={2}>
                  <NarrowEndUserMoreButton isLoading onClick={() => {}} />
                </Grid>
              )}
              {!groupResults.isLoading && groupResults.moreAvailable && (
                <Grid item xs={6} sm={4} md={3} lg={2}>
                  <NarrowEndUserMoreButton
                    onClick={() =>
                      onLoadMore(
                        groupResults.groupLabel,
                        groupResults.nextOffset,
                      )
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

EndUserFindAlternativesSearchGroupResultsView.propTypes = {
  groupResults: PropTypes.arrayOf().isRequired,
  onBack: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  calendarDay: PropTypes.string.isRequired,
  mealType: PropTypes.string,
  mealIDToReplace: PropTypes.string,
  searchCriteria: PropTypes.shape({
    searchString: PropTypes.string,
    mealTypes: PropTypes.arrayOf(PropTypes.string),
    excludeIngredients: PropTypes.arrayOf(PropTypes.string),
    includeIngredients: PropTypes.arrayOf(PropTypes.string),
    dietaryPreferences: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChangeSearchCriteria: PropTypes.func.isRequired,
};

EndUserFindAlternativesSearchGroupResultsView.defaultProps = {
  mealType: null,
  mealIDToReplace: null,
};

export default EndUserFindAlternativesSearchGroupResultsView;
